import React, { useEffect, useRef } from 'react';
import Phaser from 'phaser';

export default function Game() {
  const mountRef = useRef(null);

  useEffect(() => {
    let game;

    class MainScene extends Phaser.Scene {
      constructor() {
        super('MainScene');

        this.player = null;
        this.bullets = null;
        this.enemies = null;

        this.score = 0;
        this.lives = 3;

        this.scoreText = null;
        this.livesText = null;

        this.playerSpeed = 0;
        this.gameOver = false;
        this.gameStarted = false;

        // 🔹 Pause-related
        this.isPaused = false;
        this.pauseKey = null;
        this.pauseText = null;
        this.pauseButton = null;

        this.gameOverUI = null;
        this.startScreenUI = null;
      }

      preload() {
        this.load.image(
          'player',
          'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="32"><polygon points="24,0 48,32 0,32" fill="cyan"/></svg>'
        );
        this.load.image(
          'enemy',
          'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="24"><rect width="32" height="24" rx="4" fill="tomato"/></svg>'
        );
        this.load.image(
          'bullet',
          'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="6" height="12"><rect width="6" height="12" fill="white"/></svg>'
        );
      }

      create() {
        const w = this.scale.width;
        const h = this.scale.height;

        // Player
        this.player = this.physics.add
          .sprite(w / 2, h - 50, 'player')
          .setCollideWorldBounds(true);

        // Groups
        this.bullets = this.physics.add.group({
          classType: Phaser.Physics.Arcade.Image,
          maxSize: 20,
        });

        this.enemies = this.physics.add.group();

        // HUD
        this.scoreText = this.add.text(10, 10, 'Score: 0', {
          font: '20px Arial',
          fill: '#fff',
        });

        this.livesText = this.add.text(10, 36, 'Lives: 3', {
          font: '18px Arial',
          fill: '#fff',
        });

        // ⏸ PAUSE BUTTON (TOP RIGHT)
        this.pauseButton = this.add
          .text(w - 16, 16, '⏸ PAUSE', {
            font: '16px Arial',
            fill: '#00ffcc',
          })
          .setOrigin(1, 0)
          .setInteractive({ useHandCursor: true });

        this.pauseButton.on('pointerdown', () => {
          this.togglePause();
        });

        // PAUSE OVERLAY TEXT
        this.pauseText = this.add
          .text(w / 2, h / 2, 'PAUSED', {
            font: '48px Arial',
            fill: '#00ffcc',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setVisible(false);

        // START SCREEN
        this.startScreenUI = this.add.container(w / 2, h / 2);

        const startBg = this.add.rectangle(0, 0, 300, 200, 0x000000, 0.9);

        const startText = this.add
          .text(0, -30, 'Space War', {
            font: '28px Arial',
            fill: '#00ffcc',
            fontStyle: 'bold',
            align: 'center',
          })
          .setOrigin(0.5);

        const pressText = this.add
          .text(0, 40, 'Press Any Button to Start', {
            font: '16px Arial',
            fill: '#fff',
            align: 'center',
          })
          .setOrigin(0.5);

        this.startScreenUI.add([startBg, startText, pressText]);

        // Keyboard input (event-based movement)
        this.input.keyboard.on('keydown-LEFT', () => {
          this.playerSpeed = -300;
        });

        this.input.keyboard.on('keyup-LEFT', () => {
          if (this.playerSpeed < 0) this.playerSpeed = 0;
        });

        this.input.keyboard.on('keydown-RIGHT', () => {
          this.playerSpeed = 300;
        });

        this.input.keyboard.on('keyup-RIGHT', () => {
          if (this.playerSpeed > 0) this.playerSpeed = 0;
        });

        this.input.keyboard.on('keydown-SPACE', () => {
          this.shoot();
        });

        // Start game on any key press
        this.input.keyboard.on('keydown', (event) => {
          if (!this.gameStarted) {
            this.startGame();
          }
        });

        // Pause key
        this.pauseKey = this.input.keyboard.addKey(
          Phaser.Input.Keyboard.KeyCodes.P
        );

        this.input.keyboard.addCapture(['LEFT', 'RIGHT', 'SPACE', 'P']);

        // Enemy spawn
        this.time.addEvent({
          delay: 800,
          callback: this.spawnEnemy,
          callbackScope: this,
          loop: true,
        });

        // Collisions
        this.physics.add.overlap(
          this.bullets,
          this.enemies,
          this.handleBulletEnemy,
          null,
          this
        );

        this.physics.add.overlap(
          this.player,
          this.enemies,
          this.handlePlayerEnemy,
          null,
          this
        );
      }

      togglePause() {
        if (this.gameOver) return;

        this.isPaused = !this.isPaused;
        this.physics.world.isPaused = this.isPaused;
        this.pauseText.setVisible(this.isPaused);
        this.pauseButton.setText(this.isPaused ? '▶ RESUME' : '⏸ PAUSE');
      }

      startGame() {
        this.gameStarted = true;
        this.startScreenUI.setVisible(false);
      }

      update() {
        // Pause via keyboard
        if (Phaser.Input.Keyboard.JustDown(this.pauseKey)) {
          this.togglePause();
        }

        if (this.gameOver || this.isPaused || !this.gameStarted) return;

        this.player.setVelocityX(this.playerSpeed);

        // Cleanup bullets
        this.bullets.children.each((bullet) => {
          if (bullet && bullet.y < -10) bullet.destroy();
        });

        // Enemy passes player → lose life
        this.enemies.children.each((enemy) => {
          if (enemy && enemy.y > this.scale.height + 20) {
            enemy.destroy();
            this.loseLife();
          }
        });
      }

      shoot() {
        if (this.gameOver || this.isPaused || !this.gameStarted) return;

        const bullet = this.bullets.get(
          this.player.x,
          this.player.y - 20,
          'bullet'
        );

        if (bullet) {
          bullet.setActive(true);
          bullet.setVisible(true);
          bullet.body.enable = true;
          bullet.setVelocityY(-600);
        }
      }

      spawnEnemy() {
        if (this.gameOver || this.isPaused || !this.gameStarted) return;

        const x = Phaser.Math.Between(20, this.scale.width - 20);
        const enemy = this.enemies.create(x, -20, 'enemy');
        enemy.setVelocityY(Phaser.Math.Between(100, 200));
      }

      handleBulletEnemy(bullet, enemy) {
        bullet.destroy();
        enemy.destroy();

        this.score += 1;
        this.scoreText.setText('Score: ' + this.score);
      }

      handlePlayerEnemy(player, enemy) {
        enemy.destroy();
        this.loseLife();
      }

      loseLife() {
        if (this.gameOver) return;

        this.lives -= 1;
        this.livesText.setText('Lives: ' + this.lives);

        if (this.lives <= 0) {
          this.endGame();
        }
      }

      endGame() {
        this.gameOver = true;
        this.physics.world.isPaused = true;

        const highScore = Math.max(
          this.score,
          Number(localStorage.getItem('highScore') || 0)
        );
        localStorage.setItem('highScore', highScore);

        const w = this.scale.width;
        const h = this.scale.height;

        this.gameOverUI = this.add.container(w / 2, h / 2);

        const bg = this.add.rectangle(0, 0, 320, 260, 0x000000, 0.85);

        const text = this.add
          .text(0, -40, `GAME OVER\n\nScore: ${this.score}\nHigh Score: ${highScore}`, {
            font: '22px Arial',
            fill: '#fff',
            align: 'center',
          })
          .setOrigin(0.5);

        const restartText = this.add
          .text(0, 70, 'RESTART', {
            font: '24px Arial',
            fill: '#00ffcc',
          })
          .setOrigin(0.5)
          .setInteractive({ useHandCursor: true });

        restartText.on('pointerdown', () => {
          this.scene.restart();
          this.score = 0;
          this.lives = 3;
          this.gameOver = false;
          this.gameStarted = false;
          this.isPaused = false;
          this.physics.world.isPaused = false;
        });

        this.gameOverUI.add([bg, text, restartText]);
      }
    }

    const config = {
      type: Phaser.AUTO,
      width: 480,
      height: 640,
      parent: mountRef.current,

      audio: {
        noAudio: true, // 🔥 FIXES THE ERROR
      },

      physics: {
        default: 'arcade',
        arcade: { debug: false },
      },
        scene: MainScene,
    };

    game = new Phaser.Game(config);

    return () => game.destroy(true);
  }, []);

  return (
    <div className="game-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <h1 className="game-title"><center>Space War</center></h1>
      <div ref={mountRef} className="game-container" />

      <div className="game-attribution">
      Built by the Supreme & Nike bot developer
    </div>
    </div>
  );
}
